/**
 * Event Booking Service
 * Handles calendar bookings, conflict detection, and booking management
 */

import { db } from '@/firebase/config'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'

const BOOKINGS_COLLECTION = 'event_bookings'

/**
 * Create event booking
 */
export const createBooking = async (bookingData) => {
  try {
    const booking = {
      event_id: bookingData.event_id,
      user_id: bookingData.user_id,
      user_name: bookingData.user_name,
      user_email: bookingData.user_email,
      start_time: Timestamp.fromDate(new Date(bookingData.start_time)),
      end_time: Timestamp.fromDate(new Date(bookingData.end_time)),
      notes: bookingData.notes || '',
      status: 'confirmed', // confirmed, cancelled
      created_at: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, BOOKINGS_COLLECTION), booking)
    return { id: docRef.id, ...booking }
  } catch (error) {
    console.error('Error creating booking:', error)
    throw new Error('Failed to create booking')
  }
}

/**
 * Get all bookings for a user
 */
export const getUserBookings = async (userId) => {
  try {
    const q = query(
      collection(db, BOOKINGS_COLLECTION),
      where('user_id', '==', userId),
      where('status', '==', 'confirmed')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      start_time: doc.data().start_time?.toDate(),
      end_time: doc.data().end_time?.toDate(),
      created_at: doc.data().created_at?.toDate()
    }))
  } catch (error) {
    console.error('Error fetching user bookings:', error)
    throw new Error('Failed to fetch bookings')
  }
}

/**
 * Get all bookings for a specific event
 */
export const getEventBookings = async (eventId) => {
  try {
    const q = query(
      collection(db, BOOKINGS_COLLECTION),
      where('event_id', '==', eventId),
      where('status', '==', 'confirmed')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      start_time: doc.data().start_time?.toDate(),
      end_time: doc.data().end_time?.toDate(),
      created_at: doc.data().created_at?.toDate()
    }))
  } catch (error) {
    console.error('Error fetching event bookings:', error)
    throw new Error('Failed to fetch event bookings')
  }
}

/**
 * Check for time conflicts
 * @returns {boolean} true indicates there is a conflict
 */
export const checkTimeConflict = async (userId, startTime, endTime, excludeBookingId = null) => {
  try {
    const userBookings = await getUserBookings(userId)

    const start = new Date(startTime)
    const end = new Date(endTime)

    for (const booking of userBookings) {
      // Exclude the currently edited booking
      if (excludeBookingId && booking.id === excludeBookingId) {
        continue
      }

      const bookingStart = new Date(booking.start_time)
      const bookingEnd = new Date(booking.end_time)

      // Detect time overlap
      // Case 1: New booking start time is during existing booking
      // Case 2: New booking end time is during existing booking
      // Case 3: New booking completely contains existing booking
      if (
        (start >= bookingStart && start < bookingEnd) ||
        (end > bookingStart && end <= bookingEnd) ||
        (start <= bookingStart && end >= bookingEnd)
      ) {
        return {
          hasConflict: true,
          conflictingBooking: booking
        }
      }
    }

    return { hasConflict: false }
  } catch (error) {
    console.error('Error checking time conflict:', error)
    throw new Error('Failed to check time conflict')
  }
}

/**
 * Cancel booking
 */
export const cancelBooking = async (bookingId) => {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId)
    await updateDoc(bookingRef, {
      status: 'cancelled',
      cancelled_at: serverTimestamp()
    })
  } catch (error) {
    console.error('Error cancelling booking:', error)
    throw new Error('Failed to cancel booking')
  }
}

/**
 * Check if booking can be cancelled (cannot cancel within 12 hours before event start)
 */
export const canCancelBooking = (startTime) => {
  const now = new Date()
  const eventStart = new Date(startTime)
  const hoursUntilEvent = (eventStart - now) / (1000 * 60 * 60)

  return hoursUntilEvent >= 12
}

/**
 * Update booking notes
 */
export const updateBookingNotes = async (bookingId, notes) => {
  try {
    const bookingRef = doc(db, BOOKINGS_COLLECTION, bookingId)
    await updateDoc(bookingRef, {
      notes,
      updated_at: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating booking notes:', error)
    throw new Error('Failed to update booking notes')
  }
}

/**
 * Get all bookings within a time range (for admin view)
 */
export const getBookingsInRange = async (startDate, endDate) => {
  try {
    const q = query(
      collection(db, BOOKINGS_COLLECTION),
      where('status', '==', 'confirmed')
    )

    const snapshot = await getDocs(q)
    const bookings = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      start_time: doc.data().start_time?.toDate(),
      end_time: doc.data().end_time?.toDate(),
      created_at: doc.data().created_at?.toDate()
    }))

    // Filter bookings within specified time range
    return bookings.filter((booking) => {
      return booking.start_time >= startDate && booking.start_time <= endDate
    })
  } catch (error) {
    console.error('Error fetching bookings in range:', error)
    throw new Error('Failed to fetch bookings')
  }
}

/**
 * Export booking data to CSV format
 */
export const exportBookingsToCSV = (bookings) => {
  const headers = ['Username', 'Email', 'Event', 'Start Time', 'End Time', 'Status', 'Notes', 'Booking Time']

  const rows = bookings.map((booking) => [
    booking.user_name || 'N/A',
    booking.user_email || 'N/A',
    booking.event_title || 'N/A',
    booking.start_time ? new Date(booking.start_time).toLocaleString('en-US') : 'N/A',
    booking.end_time ? new Date(booking.end_time).toLocaleString('en-US') : 'N/A',
    booking.status === 'confirmed' ? 'Confirmed' : 'Cancelled',
    booking.notes || '',
    booking.created_at ? new Date(booking.created_at).toLocaleString('en-US') : 'N/A'
  ])

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(','))
    .join('\n')

  return '\uFEFF' + csvContent // Add BOM to support UTF-8
}

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Check if event is full
 */
export const isEventFull = async (eventId, maxCapacity) => {
  try {
    const bookings = await getEventBookings(eventId)
    return bookings.length >= maxCapacity
  } catch (error) {
    console.error('Error checking event capacity:', error)
    return false
  }
}

/**
 * Check if user already has a booking for similar event on that day
 */
export const hasDailyBooking = async (userId, eventCategory, targetDate) => {
  try {
    const userBookings = await getUserBookings(userId)

    const targetDay = new Date(targetDate).setHours(0, 0, 0, 0)

    return userBookings.some((booking) => {
      const bookingDay = new Date(booking.start_time).setHours(0, 0, 0, 0)
      return bookingDay === targetDay && booking.event_category === eventCategory
    })
  } catch (error) {
    console.error('Error checking daily booking:', error)
    return false
  }
}
